import Vue from 'vue';
import { Route } from 'vue-router';
import { ActionTree, MutationTree } from 'vuex';

import type { ApolloProvider } from 'vue-apollo';

import { CollectionType } from '@/enums/collection-type';

import { getPageType } from '@/helpers/tracking/tracking-helper';
import { RouterMeta } from '@/routing/config';
import { OnboardingStatus } from '@/enums/onboarding-status';
import { Domain } from '@/routing/helpers/subdomain-helper';

import {
	GetUrlMetadataDocument,
	GetUrlMetadataQuery,
	GetUrlMetadataQueryVariables,
} from '@/graphql/query/GetUrlMetadata.query';
import { ObjectType } from '@/@types/graphql-types';

// Type alias for URL metadata from GraphQL, extended with routing store additions
export type UrlMetadata = GetUrlMetadataQuery['urlV2'] & {
	allHrefLangTags?: Array<{ href: string; hrefLang: string; locale: string }>;
};

export type ListsCollectionType =
	| CollectionType.MY_LISTS
	| CollectionType.PUBLIC_LISTS
	| CollectionType.TV_SHOW_TRACKING
	| CollectionType.MY_RENTALS;

export type RoutingState = {
	activeRoute: Route | null;
	// this is used to fix the lazy loading of components with ion tabs.
	// [tab]: true when activated once
	activatedTabs: Record<string, boolean>;
	activeDomain: Domain;
	lastActiveCollectionType: CollectionType | null;
	lastActiveListsCollectionType: ListsCollectionType | null;
	lastRoute: Route | null;
	lastUrlMetadata: UrlMetadata | null;
	pageType: string;
	scrollPositions: Partial<Record<RouterMeta['tab'], number>>;
	urlMetadata: UrlMetadata | null;
	// TODO We're assuming implicitly this would only be called after promise is registered. That should not be the case.
	// An event that other modules can register to - so they get the data when it's there.
	urlMetadataPromise: Promise<UrlMetadata> | null;
};

type RoutingGetter<T> = (state: RoutingState, getters: any) => T;

export type RoutingGetters = {
	activeCollectionType: RoutingGetter<CollectionType | null>;
	currentPageType: RoutingGetter<string>;
	lastActiveCollectionType: RoutingGetter<CollectionType | null>;
	lastActiveListsCollectionType: RoutingGetter<ListsCollectionType | null>;
	queryParams: RoutingGetter<Route['query']>;
	urlMetadata: RoutingGetter<RoutingState['urlMetadata']>;
	activeDomain: RoutingGetter<RoutingState['activeDomain']>;
};

export type RoutingStore = {
	state: RoutingState;
	getters: RoutingGetters;
};

const state = () =>
	({
		activatedTabs: {},
		activeRoute: null,
		activeDomain: Domain.MAIN,
		pageType: 'VIEW_HOME', // default url
		lastActiveCollectionType: null,
		lastActiveListsCollectionType: null,
		lastRoute: null,
		lastUrlMetadata: null,
		scrollPositions: {},
		urlMetadata: null,
		urlMetadataPromise: new Promise(() => {}),
	} as RoutingState);

const getters: RoutingGetters = {
	activeCollectionType(state): CollectionType | null {
		return state.activeRoute ? (state.activeRoute.meta || {}).collectionType : null;
	},
	currentPageType(state): string {
		return state.pageType;
	},
	lastActiveCollectionType(state, getters): CollectionType | null {
		const currentCollectionType = getters.activeCollectionType;
		if (currentCollectionType) {
			return currentCollectionType;
		}

		const { lastActiveCollectionType } = state;
		if (lastActiveCollectionType) {
			return lastActiveCollectionType;
		}

		// This is a failsafe, it should never land here. Gotta investigate or rewrite this
		return CollectionType.GLOBAL;
	},
	lastActiveListsCollectionType(state): ListsCollectionType | null {
		return state.lastActiveListsCollectionType;
	},
	queryParams(state): Route['query'] {
		if (state.activeRoute) {
			// TODO Unsure if casting makes sense here.
			return state.activeRoute.query as Dictionary<string | string[]>;
		} else {
			return {};
		}
	},
	urlMetadata(state): RoutingState['urlMetadata'] {
		const urlMetadata = (state.urlMetadata || {}) as UrlMetadata;
		const heading1 = urlMetadata.heading1;
		return {
			...urlMetadata,
			...{ heading1 },
		};
	},

	activeDomain(state) {
		return state.activeDomain;
	},
};

const actions: ActionTree<RoutingState, any> = {
	async fetchUrlMetadata(
		{ commit, state, rootState, rootGetters },
		payload: string | { url: string; apolloProvider: ApolloProvider }
	) {
		const url = typeof payload === 'string' ? payload : payload.url;
		const apolloProvider = typeof payload === 'object' ? payload.apolloProvider : null;

		let path = url.split('?')[0];
		if (path !== '/' && !path.includes('shared')) {
			path = path.replace(/\/$/, ''); // remove trailing backslash
			try {
				let promise: Promise<GetUrlMetadataQuery | { urlV2: null }>;

				// Check if Apollo provider is available and has defaultClient
				if (apolloProvider?.defaultClient) {
					// Use GraphQL query via Apollo
					promise = apolloProvider.defaultClient
						.query<GetUrlMetadataQuery, GetUrlMetadataQueryVariables>({
							query: GetUrlMetadataDocument,
							variables: {
								fullPath: path,
								site: state.activeDomain,
							},
						})
						.then(({ data }) => data);
				} else {
					// Apollo provider is not available - skip GraphQL query
					promise = Promise.resolve({ urlV2: null });
				}

				commit('SET_URL_METADATA_PROMISE', promise);

				const result = await promise;

				commit('SET_LAST_URL_METADATA', state.urlMetadata);
				commit('SET_URL_METADATA', result?.urlV2 ?? {});
			} catch (err) {
				console.error('[RoutingStore] fetchUrlMetadata', url, err);
				commit('SET_URL_METADATA_PROMISE', Promise.resolve());
			}

			// @todo netflix 4k exception
			// @todo maybe set customCanonical to meta.store.ts state?
		} else {
			const onboardingStatus = rootState.user.onboardingStatus;
			const technicalName =
				state.activeDomain === Domain.EDITORIAL
					? 'VIEW_HOME_GUIDES'
					: onboardingStatus === OnboardingStatus.GLOBAL
					? 'VIEW_HOME_GLOBAL'
					: 'VIEW_HOME';
			const homeMockMeta: UrlMetadata = {
				id: 'home',
				urlID: 0,
				locale: rootGetters['language/locale'],
				objectType: ObjectType.Page,
				fullPath: '/',
				site: state.activeDomain,
				metaTitle: 'JustWatch - The Streaming Guide',
				metaDescription: 'All your streaming services in one app.',
				metaKeywords: '',
				page: { technicalName },
				heading1: null,
				heading2: null,
				metaRobots: '',
				hrefLangTags: [],
				allHrefLangTags: [],
			};

			commit('SET_LAST_URL_METADATA', state.urlMetadata);
			commit('SET_URL_METADATA', homeMockMeta);
			commit('SET_URL_METADATA_PROMISE', Promise.resolve());
		}

		commit('SET_PAGE_TYPE', getPageType(state.urlMetadata));
	},

	async setActiveRoute({ commit }, { activeRoute }: { activeRoute: Route }) {
		const meta = { ...activeRoute.meta };

		// exclude 'matched' that causes circular dependency
		/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
		const { matched, ...route } = activeRoute;
		commit('SET_ACTIVE_ROUTE', { ...route, path: decodeURI(route.path), meta });
		commit('ACTIVATE_TAB', activeRoute?.meta?.tab);
	},
	setLastActiveListsCollectionType({ commit }, lastActiveListsCollectionType: ListsCollectionType) {
		commit('SET_LAST_ACTIVE_LISTS_COLLECTION_TYPE', lastActiveListsCollectionType);
	},

	setActiveDomain({ commit }, value) {
		commit('SET_ACTIVE_DOMAIN', value);
	},
};

const mutations: MutationTree<RoutingState> = {
	ACTIVATE_TAB(state, tab: string) {
		if (tab) {
			state.activatedTabs = {
				...state.activatedTabs,
				[tab]: true,
			};
		}
	},
	SET_ACTIVE_ROUTE(state, activeRoute) {
		Vue.set(state, 'lastRoute', { ...state.activeRoute });
		Vue.set(state, 'activeRoute', { ...activeRoute });
		// only set lastActiveCollectionType of the last route that had a collectionType
		if (activeRoute && (activeRoute.meta || {}).collectionType) {
			Vue.set(state, 'lastActiveCollectionType', activeRoute.meta.collectionType);
		}
	},
	SET_LAST_ACTIVE_LISTS_COLLECTION_TYPE(state, lastActiveListsCollectionType: ListsCollectionType) {
		state.lastActiveListsCollectionType = lastActiveListsCollectionType;
	},
	SET_LAST_URL_METADATA(state, lastUrlMetadata) {
		state.lastUrlMetadata = lastUrlMetadata;
	},
	SET_PAGE_TYPE(state, pageType) {
		state.pageType = pageType;
	},
	SET_URL_METADATA(state, urlMetadata: GetUrlMetadataQuery['urlV2']) {
		// Ensure urlMetadata is not null/undefined to prevent destructuring errors
		const safeUrlMetadata = urlMetadata ?? {};
		const { metaRobots = '' } = safeUrlMetadata;
		const hasNoIndex = metaRobots?.includes('noindex');
		const hrefLangTags = (safeUrlMetadata?.hrefLangTags || [])
			// make sure we don't include urls that have noindex
			.filter(tag => !tag.hasNoindex && !hasNoIndex);

		state.urlMetadata = {
			...safeUrlMetadata,
			hrefLangTags,
			allHrefLangTags: safeUrlMetadata?.hrefLangTags || [],
		};
	},
	SET_URL_METADATA_PROMISE(state, urlMetadataPromise) {
		state.urlMetadataPromise = urlMetadataPromise;
	},
	SET_SCROLL_POSITION(state, { tab, y }: { tab: RouterMeta['tab']; y: number }) {
		if (tab) Vue.set(state, 'scrollPositions', { ...state.scrollPositions, [tab]: y });
	},

	SET_ACTIVE_DOMAIN(state, value: Domain) {
		state.activeDomain = value;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
